import { ShieldCheck, DoorClosed, Hand, EyeOff } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";

const PILLARS = [
  {
    Icon: DoorClosed,
    title: "Nothing shows until you tap in",
    body:
      "Your profile only appears inside a GateRoom after you check in. Walk out and you disappear from the room.",
  },
  {
    Icon: EyeOff,
    title: "Room-scoped visibility",
    body:
      "You're seen only by people at the same venue at the same time. No city-wide broadcast, no location on your profile.",
  },
  {
    Icon: Hand,
    title: "Block, mute, report — one tap",
    body:
      "A single tap ends any thread. Reports go straight to GateMate's moderation team, reviewed within 24 hours. Bad actors lose access to the room and the app.",
  },
  {
    Icon: ShieldCheck,
    title: "Your data stays yours",
    body:
      "No background tracking. No location history. No selling contacts. No ad networks. Delete your account and your profile, messages, photos and connections go with it — the only thing we hold back is content that's already been reported, kept up to 30 days so a human can act on the report.",
  },
];

export default function SafetyPanel() {
  return (
    <section
      id="safety"
      className="relative py-24 md:py-28 overflow-hidden"
      data-testid="safety-panel"
    >
      <div className="glow-orb w-[420px] h-[420px] bg-neon-cyan/10 top-10 -left-20" />
      <div className="glow-orb w-[360px] h-[360px] bg-neon-violet/10 bottom-0 right-0" />

      <div className="container-x relative z-10">
        <Reveal className="max-w-3xl">
          <p className="overline mb-4">Your night, your rules</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95]">
            You're always in <span className="text-gradient">control</span>.
          </h2>
          <p className="mt-4 text-[15px] text-neon-cyan font-medium" data-testid="privacy-founder-line">
            Built by a cybersecurity professor — privacy isn't a feature, it's the foundation.
          </p>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed max-w-2xl">
            GateMate is opt-in the whole way through. You decide when to appear, who to talk to,
            and what happens after the lights come up.
          </p>
        </Reveal>

        <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map(({ Icon, title, body }) => (
            <StaggerItem key={title}>
              <div className="h-full glass rounded-2xl p-6 hover:glass-cyan transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-neon-cyan/25 to-neon-violet/25 grid place-items-center mb-4 shadow-[0_0_24px_rgba(0,198,174,0.2)]">
                  <Icon className="w-5 h-5 text-neon-cyan" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white mb-2 leading-snug">
                  {title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
